import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import { Plus, Pencil, Trash2, Search, X, Key, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnersManagement = () => {
 const { user } = useAuth();
 const { selectedBranch } = useApp();
 
 // Check if user is owner or kepala_cabang for branch filtering
 const isOwnerOrKepala = user?.role === 'owner' || user?.role === 'kepala_cabang';
 const userBranchId = user?.branch_id;
 
 const [loading, setLoading] = useState(true);
 const [owners, setOwners] = useState([]);
 const [branches, setBranches] = useState([]);
 const [roles, setRoles] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [showModal, setShowModal] = useState(false);
 const [editMode, setEditMode] = useState(false);
 const [currentOwner, setCurrentOwner] = useState(null);
 const [processing, setProcessing] = useState(false);
 const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

 const handleSort = (key) => {
 setSortConfig(prev => prev.key === key
 ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
 : { key, direction: 'asc' });
 };

 const getSortIcon = (key) => {
 if (sortConfig.key !== key) return <ArrowUpDown className="inline w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
 return sortConfig.direction === 'asc'
 ? <ArrowUp className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
 : <ArrowDown className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
 };
 
 // Pagination state
 const [currentPage, setCurrentPage] = useState(1);
 const [itemsPerPage, setItemsPerPage] = useState(10);
 
 // Reset Password Modal State
 const [showResetModal, setShowResetModal] = useState(false);
 const [resetOwner, setResetOwner] = useState(null);
 const [newPassword, setNewPassword] = useState('');
 const [resetting, setResetting] = useState(false);
 
 // Delete Modal State
 const [showDeleteModal, setShowDeleteModal] = useState(false);
 const [deleteOwner, setDeleteOwner] = useState(null);
 const [deleting, setDeleting] = useState(false);
 const [deletePreview, setDeletePreview] = useState(null);
 const [deleteConfirmText, setDeleteConfirmText] = useState('');
 const [loadingPreview, setLoadingPreview] = useState(false);

 const [formData, setFormData] = useState({
 name: '',
 phone: '',
 email: '',
 branch_id: '',
 role: 'owner',
 fee_type: 'percentage',
 fee_value: '0'
 });

 useEffect(() => {
 fetchData();
 }, [selectedBranch]);

 const fetchData = async () => {
 try {
 setLoading(true);
 const token = localStorage.getItem('token');
 
 // Build owners URL with branch filter
 let ownersUrl = `${API_URL}/api/v2/owners`;
 const params = new URLSearchParams();
 
 // Apply branch filter - either from user role or admin selection
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 
 if (params.toString()) {
 ownersUrl += `?${params.toString()}`;
 }

 const [ownersRes, branchesRes, rolesRes] = await Promise.all([
 axios.get(ownersUrl, {
 headers: { Authorization: `Bearer ${token}` }
 }),
 axios.get(`${API_URL}/api/v2/branches`, {
 headers: { Authorization: `Bearer ${token}` }
 }),
 axios.get(`${API_URL}/api/v2/roles`, {
 headers: { Authorization: `Bearer ${token}` }
 })
 ]);

 if (ownersRes.data.success) {
 setOwners(ownersRes.data.data);
 }

 if (branchesRes.data.success) {
 setBranches(branchesRes.data.data);
 }

 if (rolesRes.data.success) {
 setRoles(rolesRes.data.data || []);
 }
 } catch (error) {
 console.error('Error fetching data:', error);
 toast.error('Gagal memuat data');
 } finally {
 setLoading(false);
 }
 };

 const handleOpenModal = (owner = null) => {
 if (owner) {
 setEditMode(true);
 setCurrentOwner(owner);
 setFormData({
 name: owner.name || '',
 phone: owner.phone || '',
 email: owner.email || '',
 branch_id: owner.branch_id || '',
 role: owner.role || 'owner',
 fee_type: owner.fee_type || 'percentage',
 fee_value: owner.fee_value?.toString() || (owner.fee_percentage?.toString() || '0')
 });
 } else {
 setEditMode(false);
 setCurrentOwner(null);
 // For owner/kepala_cabang, auto-set their branch_id
 setFormData({
 name: '',
 phone: '',
 email: '',
 branch_id: isOwnerOrKepala ? userBranchId : '',
 role: 'owner',
 fee_type: 'percentage',
 fee_value: '0'
 });
 }
 setShowModal(true);
 };

 const handleCloseModal = () => {
 setShowModal(false);
 setEditMode(false);
 setCurrentOwner(null);
 setFormData({
 name: '',
 phone: '',
 email: '',
 branch_id: isOwnerOrKepala ? userBranchId : '',
 role: 'owner',
 fee_type: 'percentage',
 fee_value: '0'
 });
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!safeTrim(formData.name)) {
 toast.error('Nama owner wajib diisi');
 return;
 }

 // Validate phone number - ONLY NUMBERS
 if (formData.phone && !/^[0-9]+$/.test(formData.phone)) {
 toast.error('Nomor telepon harus berisi angka saja!');
 return;
 }

 if (formData.phone && formData.phone.length < 10) {
 toast.error('Nomor telepon minimal 10 digit');
 return;
 }

 try {
 setProcessing(true);
 const token = localStorage.getItem('token');

 if (editMode && currentOwner) {
 // Update owner
 const response = await axios.put(
 `${API_URL}/api/v2/owners/${currentOwner.id}`,
 formData,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('✅ Owner berhasil diupdate');
 handleCloseModal();
 fetchData();
 }
 } else {
 // Create owner
 const response = await axios.post(
 `${API_URL}/api/v2/owners`,
 formData,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('✅ Owner berhasil ditambahkan');
 handleCloseModal();
 fetchData();
 }
 }
 } catch (error) {
 console.error('Error saving owner:', error);
 toast.error(error.response?.data?.detail || 'Gagal menyimpan data');
 } finally {
 setProcessing(false);
 }
 };

 const openDeleteModal = async (owner) => {
 setDeleteOwner(owner);
 setShowDeleteModal(true);
 setDeletePreview(null);
 setDeleteConfirmText('');
 setLoadingPreview(true);
 try {
 const token = localStorage.getItem('token');
 const res = await axios.get(`${API_URL}/api/v2/owners/${owner.id}/delete-preview`, {
 headers: { Authorization: `Bearer ${token}` }
 });
 if (res.data?.success) setDeletePreview(res.data.data);
 } catch (e) {
 console.error('preview err', e);
 } finally {
 setLoadingPreview(false);
 }
 };

 const handleDelete = async () => {
 if (!deleteOwner) return;
 setDeleting(true);
 try {
 const token = localStorage.getItem('token');
 // Default: soft delete (move to trash, reversible 30 days)
 const url = `${API_URL}/api/v2/owners/${deleteOwner.id}`;
 const response = await axios.delete(url, {
 headers: { Authorization: `Bearer ${token}` }
 });
 if (response.data.success) {
 if (response.data.soft_delete) {
 toast.success('✅ Owner dipindahkan ke Tempat Sampah');
 } else {
 toast.success('✅ Owner berhasil dihapus');
 }
 setShowDeleteModal(false);
 setDeleteOwner(null);
 setDeletePreview(null);
 setDeleteConfirmText('');
 fetchData();
 }
 } catch (error) {
 console.error('Error deleting owner:', error);
 toast.error(error.response?.data?.detail || 'Gagal menghapus owner');
 } finally {
 setDeleting(false);
 }
 };

 const openResetPasswordModal = (owner) => {
 setResetOwner(owner);
 setNewPassword('');
 setShowResetModal(true);
 };

 const handleResetPassword = async () => {
 if (!resetOwner) return;
 
 const passwordToSet = newPassword.trim() || '12345678';
 setResetting(true);
 
 try {
 const token = localStorage.getItem('token');
 const response = await axios.post(
 `${API_URL}/api/v2/owners/${resetOwner.id}/reset-password`,
 { new_password: passwordToSet },
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success(`✅ Password berhasil direset!`);
 setShowResetModal(false);
 setResetOwner(null);
 setNewPassword('');
 }
 } catch (error) {
 console.error('Error resetting password:', error);
 toast.error(error.response?.data?.detail || 'Gagal reset password');
 } finally {
 setResetting(false);
 }
 };

 let filteredOwners = owners.filter(owner =>
 owner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 owner.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 owner.email?.toLowerCase().includes(searchTerm.toLowerCase())
 );
 if (sortConfig.key) {
 const key = sortConfig.key;
 const dir = sortConfig.direction === 'asc' ? 1 : -1;
 filteredOwners = [...filteredOwners].sort((a, b) => {
 let av, bv;
 if (key === '_owner_id') {
 av = parseInt(String(a.owner_code || a.id || '').replace(/[^0-9]/g, '')) || 0;
 bv = parseInt(String(b.owner_code || b.id || '').replace(/[^0-9]/g, '')) || 0;
 } else if (key === '_branch') {
 av = (a.branch_name || a.branch || '').toLowerCase();
 bv = (b.branch_name || b.branch || '').toLowerCase();
 } else if (key === '_fee') {
 av = Number(a.fee_percentage ?? a.fee ?? 0) || 0;
 bv = Number(b.fee_percentage ?? b.fee ?? 0) || 0;
 } else if (key === '_saldo') {
 av = Number(a.balance ?? a.saldo ?? 0) || 0;
 bv = Number(b.balance ?? b.saldo ?? 0) || 0;
 } else {
 av = a[key]; bv = b[key];
 if (typeof av === 'string') av = av.toLowerCase();
 if (typeof bv === 'string') bv = bv.toLowerCase();
 }
 if (av == null) av = '';
 if (bv == null) bv = '';
 if (av < bv) return -1 * dir;
 if (av > bv) return 1 * dir;
 return 0;
 });
 }

 // Pagination calculations
 const totalItems = filteredOwners.length;
 const totalPages = Math.ceil(totalItems / itemsPerPage);
 const startIndex = (currentPage - 1) * itemsPerPage;
 const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
 const paginatedOwners = filteredOwners.slice(startIndex, endIndex);

 // Reset to page 1 when search changes
 useEffect(() => {
 setCurrentPage(1);
 }, [searchTerm]);

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
 <p className="text-gray-600 dark:text-gray-400">Memuat data...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6 ">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manajemen Owner</h1>
 <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
 Kelola data owner dengan fee custom
 </p>
 </div>
 <Button
 onClick={() => handleOpenModal()}
 variant="outline"
 className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
 >
 <Plus className="h-4 w-4" />
 Tambah Owner
 </Button>
 </div>

 {/* Table Card */}
 <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle className="text-gray-900 dark:text-white">Daftar Owner ({filteredOwners.length})</CardTitle>
 <div className="relative w-64">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
 <Input
 placeholder="Cari owner..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
 />
 </div>
 </div>
 </CardHeader>
 <CardContent>
 {/* Pagination Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-2 py-3 border-b border-slate-200 dark:border-slate-700 gap-2 mb-2">
 <div className="flex items-center gap-2">
 <span className="text-xs text-gray-600 dark:text-gray-400">Tampilkan</span>
 <select
 value={itemsPerPage}
 onChange={(e) => {
 setItemsPerPage(Number(e.target.value));
 setCurrentPage(1);
 }}
 className="px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 <option value={100}>100</option>
 </select>
 <span className="text-xs text-gray-600 dark:text-gray-400">data</span>
 </div>
 <div className="text-xs text-gray-600 dark:text-gray-400">
 Menampilkan {totalItems > 0 ? startIndex + 1 : 0} - {endIndex} dari {totalItems} owner
 </div>
 </div>

 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700">
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('_owner_id')}>ID Owner{getSortIcon('_owner_id')}</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('name')}>Nama{getSortIcon('name')}</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('phone')}>Telepon{getSortIcon('phone')}</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('email')}>Email{getSortIcon('email')}</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('_branch')}>Cabang{getSortIcon('_branch')}</th>
 <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('role')}>Role{getSortIcon('role')}</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('_fee')}>Fee{getSortIcon('_fee')}</th>
 <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-slate-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 select-none" onClick={() => handleSort('_saldo')}>Saldo{getSortIcon('_saldo')}</th>
 <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-slate-200">Aksi</th>
 </tr>
 </thead>
 <tbody>
 {paginatedOwners.length === 0 ? (
 <tr>
 <td colSpan="9" className="text-center p-8 text-gray-500 dark:text-gray-400">
 {searchTerm ? 'Tidak ada hasil' : 'Belum ada data owner'}
 </td>
 </tr>
 ) : (
 paginatedOwners.map((owner) => {
 const branch = branches.find(b => b.id === owner.branch_id);
 return (
 <tr key={owner.id} className="border-b border-slate-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
 <td className="py-2 px-3">
 <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
 {owner.owner_code || '-'}
 </span>
 </td>
 <td className="py-2 px-3">
 <div className="font-medium text-gray-900 dark:text-white">{owner.name}</div>
 </td>
 <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{owner.phone || '-'}</td>
 <td className="py-2 px-3 text-gray-600 dark:text-gray-300 truncate max-w-[150px]" title={owner.email}>
 {owner.email || '-'}
 </td>
 <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{branch?.name || '-'}</td>
 <td className="py-2 px-3">
 <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
 owner.role === 'admin' ? 'border-purple-500 text-purple-600 dark:text-purple-400' :
 owner.role === 'kepala_cabang' ? 'border-amber-500 text-amber-600 dark:text-amber-400' :
 owner.role === 'customer_service' ? 'border-blue-500 text-blue-600 dark:text-blue-400' :
 owner.role === 'teknisi' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' :
 'border-green-500 text-green-600 dark:text-green-400'
 }`}>
 {owner.role === 'kepala_cabang' ? 'Kepala Cabang' : 
 owner.role ? owner.role.charAt(0).toUpperCase() + owner.role.slice(1) : 'Owner'}
 </span>
 </td>
 <td className="py-2 px-3 text-right">
 <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border border-blue-500 text-blue-600 dark:text-blue-400">
 {owner.fee_type === 'fixed' 
 ? `Rp ${(owner.fee_value || 0).toLocaleString('id-ID')}`
 : `${owner.fee_value || owner.fee_percentage || 0}%`
 }
 </span>
 </td>
 <td className="py-2 px-3 text-right font-semibold text-green-600 dark:text-green-400">
 Rp {(owner.balance || 0).toLocaleString('id-ID')}
 </td>
 <td className="py-2 px-3">
 <div className="flex items-center justify-center gap-1.5">
 <Button
 size="sm"
 variant="outline"
 onClick={() => handleOpenModal(owner)}
 className="h-7 w-7 p-0"
 title="Edit Owner"
 >
 <Pencil className="h-3 w-3" />
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openResetPasswordModal(owner)}
 className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30 dark:text-amber-400"
 title="Reset Password"
 disabled={!owner.owner_code}
 data-testid={`owner-reset-btn-${owner.id}`}
 >
 <Key className="h-3 w-3" />
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openDeleteModal(owner)}
 className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 dark:text-red-400"
 title="Hapus Owner"
 >
 <Trash2 className="h-3 w-3" />
 </Button>
 </div>
 </td>
 </tr>
 );
 })
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination Footer */}
 {totalPages > 1 && (
 <div className="flex flex-col sm:flex-row items-center justify-between px-2 py-3 border-t border-slate-200 dark:border-slate-700 mt-2 gap-2">
 <div className="text-xs text-gray-600 dark:text-gray-400">
 Halaman {currentPage} dari {totalPages}
 </div>
 <div className="flex items-center gap-1">
 <Button
 size="sm"
 variant="outline"
 onClick={() => setCurrentPage(1)}
 disabled={currentPage === 1}
 className="px-2 h-8"
 >
 «
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
 disabled={currentPage === 1}
 className="h-8"
 >
 <ChevronLeft className="w-4 h-4" />
 </Button>
 
 {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
 let pageNum;
 if (totalPages <= 5) {
 pageNum = i + 1;
 } else if (currentPage <= 3) {
 pageNum = i + 1;
 } else if (currentPage >= totalPages - 2) {
 pageNum = totalPages - 4 + i;
 } else {
 pageNum = currentPage - 2 + i;
 }
 return (
 <Button
 key={pageNum}
 size="sm"
 variant={currentPage === pageNum ? "default" : "outline"}
 onClick={() => setCurrentPage(pageNum)}
 className={`px-3 h-8 ${currentPage === pageNum ? 'bg-indigo-600 text-white' : ''}`}
 >
 {pageNum}
 </Button>
 );
 })}
 
 <Button
 size="sm"
 variant="outline"
 onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
 disabled={currentPage === totalPages}
 className="h-8"
 >
 <ChevronRight className="w-4 h-4" />
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => setCurrentPage(totalPages)}
 disabled={currentPage === totalPages}
 className="px-2 h-8"
 >
 »
 </Button>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Modal Form */}
 {showModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
 {editMode ? 'Edit Owner' : 'Tambah Owner Baru'}
 </h3>
 <button
 onClick={handleCloseModal}
 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
 >
 <X className="h-5 w-5" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="space-y-3">
 {/* Nama */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Nama Owner <span className="text-red-500">*</span>
 </label>
 <Input
 value={formData.name}
 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
 placeholder="Nama lengkap"
 className="text-sm h-9"
 required
 />
 </div>

 {/* Telepon */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Nomor Telepon
 </label>
 <Input
 type="tel"
 value={formData.phone}
 onChange={(e) => {
 const value = e.target.value.replace(/[^0-9]/g, '');
 setFormData({ ...formData, phone: value });
 }}
 placeholder="08123456789"
 pattern="[0-9]*"
 inputMode="numeric"
 className="text-sm h-9"
 />
 <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
 Hanya angka. Contoh: 08123456789
 </p>
 </div>

 {/* Email */}
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Email
 </label>
 <Input
 type="email"
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 placeholder="owner@example.com"
 className="text-sm h-9"
 />
 </div>

 {/* Cabang & Role - 2 columns */}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Cabang *
 </label>
 {isOwnerOrKepala ? (
 // For owner/kepala_cabang - show their branch only (read-only)
 <div className="w-full px-3 py-2 text-sm h-9 border border-gray-300 rounded-md bg-gray-100 text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
 {branches.find(b => b.id === userBranchId)?.name || 'Cabang Anda'}
 </div>
 ) : (
 // For admin - show all branches
 <select
 value={formData.branch_id}
 onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
 className="w-full px-3 py-2 text-sm h-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
 >
 <option value="">-- Pilih Cabang --</option>
 {branches.map(branch => (
 <option key={branch.id} value={branch.id}>
 {branch.name}
 </option>
 ))}
 </select>
 )}
 </div>
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Role *
 </label>
 <select
 value={formData.role}
 onChange={(e) => setFormData({ ...formData, role: e.target.value })}
 className="w-full px-3 py-2 text-sm h-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
 >
 <option value="owner">Owner (Default)</option>
 {roles.filter(r => r.name !== 'owner').map(role => (
 <option key={role.id} value={role.name}>
 {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
 </option>
 ))}
 </select>
 </div>
 </div>

 {/* Fee Type & Value - 2 columns */}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 Tipe Fee
 </label>
 <select
 value={formData.fee_type}
 onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
 className="w-full px-3 py-2 text-sm h-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
 >
 <option value="percentage">Persentase (%)</option>
 <option value="fixed">Nominal (Rp)</option>
 </select>
 </div>

 <div>
 <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1.5">
 {formData.fee_type === 'percentage' ? 'Fee (%)' : 'Fee (Rp)'}
 </label>
 <Input
 type="number"
 value={formData.fee_value}
 onChange={(e) => setFormData({ ...formData, fee_value: e.target.value })}
 placeholder={formData.fee_type === 'percentage' ? '0' : '5000'}
 min="0"
 max={formData.fee_type === 'percentage' ? '100' : undefined}
 step={formData.fee_type === 'percentage' ? '0.1' : '1000'}
 className="text-sm h-9"
 />
 </div>
 </div>
 <p className="text-xs text-gray-500 -mt-2 dark:text-slate-400">
 {formData.fee_type === 'percentage' 
 ? 'Fee % yang didapat owner per transaksi' 
 : 'Fee tetap (Rp) per transaksi'}
 </p>

 {/* Actions */}
 <div className="flex gap-2 pt-3 border-t mt-4">
 <Button
 type="button"
 variant="outline"
 onClick={handleCloseModal}
 className="flex-1 h-9 text-sm"
 disabled={processing}
 >
 Batal
 </Button>
 <Button
 type="submit"
 disabled={processing}
 className="flex-1 h-9 text-sm bg-indigo-600 hover:bg-indigo-700"
 >
 {processing ? 'Menyimpan...' : editMode ? 'Update' : 'Simpan'}
 </Button>
 </div>
 </form>
 </div>
 </div>
 )}

 {/* Delete Confirmation Modal */}
 {showDeleteModal && deleteOwner && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
 <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden dark:bg-slate-800">
 {/* Header */}
 <div className="bg-gradient-to-r from-red-500 to-rose-500 px-5 py-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-white/20 rounded-lg">
 <Trash2 className="h-5 w-5 text-white" />
 </div>
 <h3 className="text-lg font-semibold text-white">Hapus Owner</h3>
 </div>
 <button 
 onClick={() => { setShowDeleteModal(false); setDeletePreview(null); setDeleteConfirmText(''); }}
 className="text-white/80 hover:text-white transition-colors"
 >
 <X className="h-5 w-5" />
 </button>
 </div>
 </div>
 
 {/* Body */}
 <div className="p-5">
 {/* Owner info */}
 <div className="bg-gray-50 rounded-lg p-3 mb-4 dark:bg-slate-900">
 <p className="font-semibold text-gray-900 dark:text-slate-100">{deleteOwner.name}</p>
 {deleteOwner.owner_code && (
 <p className="text-xs text-gray-500 dark:text-slate-400">ID: <span className="font-mono text-indigo-600 dark:text-indigo-400">{deleteOwner.owner_code}</span></p>
 )}
 {deleteOwner.email && <p className="text-xs text-gray-500 dark:text-slate-400">{deleteOwner.email}</p>}
 </div>

 {/* Impact preview */}
 {loadingPreview ? (
 <div className="flex items-center justify-center py-6">
 <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
 <span className="ml-2 text-sm text-gray-500 dark:text-slate-400">Memuat data terkait...</span>
 </div>
 ) : deletePreview && (deletePreview.customers > 0 || deletePreview.invoices.count > 0) ? (
 <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 dark:bg-amber-900/30">
 <p className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1 dark:text-amber-300">
 <Trash2 className="h-4 w-4" /> Data terkait owner ini:
 </p>
 <div className="space-y-1 text-xs text-amber-700 dark:text-amber-300" data-testid="delete-impact-list">
 <div className="flex justify-between"><span>Pelanggan:</span><span className="font-bold">{deletePreview.customers}</span></div>
 <div className="flex justify-between"><span>Invoice:</span><span className="font-bold">{deletePreview.invoices.count} (Rp {Number(deletePreview.invoices.total).toLocaleString('id-ID')})</span></div>
 <div className="flex justify-between"><span>Pembayaran:</span><span className="font-bold">{deletePreview.payments.count} (Rp {Number(deletePreview.payments.total).toLocaleString('id-ID')})</span></div>
 <div className="flex justify-between"><span>Tiket gangguan:</span><span className="font-bold">{deletePreview.tickets}</span></div>
 </div>
 <p className="text-[11px] text-amber-600 mt-2 dark:text-amber-400">Data tetap utuh selama owner di Tempat Sampah.</p>
 </div>
 ) : null}
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
 <p className="text-xs text-blue-800">
 💡 Owner akan masuk ke <b>Tempat Sampah</b>. Anda dapat memulihkannya dalam <b>30 hari</b> sebelum auto-purge permanen.
 </p>
 </div>
 </div>

 {/* Footer */}
 <div className="px-5 py-4 bg-gray-50 flex gap-3 dark:bg-slate-900">
 <Button
 variant="outline"
 onClick={() => { setShowDeleteModal(false); setDeletePreview(null); setDeleteConfirmText(''); }}
 className="flex-1"
 disabled={deleting}
 >
 Batal
 </Button>
 <Button
 onClick={handleDelete}
 disabled={deleting || loadingPreview}
 data-testid="delete-owner-confirm-btn"
 className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
 >
 {deleting ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
 Menghapus...
 </>
 ) : (
 <>
 <Trash2 className="h-4 w-4 mr-2" />
 Ya, Hapus
 </>
 )}
 </Button>
 </div>
 </div>
 </div>
 )}

 {/* Reset Password Modal */}
 {showResetModal && resetOwner && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
 <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden dark:bg-slate-800">
 {/* Header */}
 <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="p-2 bg-white/20 rounded-lg">
 <Key className="h-5 w-5 text-white" />
 </div>
 <h3 className="text-lg font-semibold text-white">Reset Password</h3>
 </div>
 <button 
 onClick={() => setShowResetModal(false)}
 className="text-white/80 hover:text-white transition-colors"
 >
 <X className="h-5 w-5" />
 </button>
 </div>
 </div>
 
 {/* Body */}
 <div className="p-5">
 {/* Owner Info */}
 <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-slate-900">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center dark:bg-indigo-900/40">
 <span className="text-indigo-600 font-semibold text-sm dark:text-indigo-400">
 {resetOwner.name?.charAt(0)?.toUpperCase()}
 </span>
 </div>
 <div>
 <p className="font-semibold text-gray-900 dark:text-slate-100">{resetOwner.name}</p>
 <p className="text-xs text-gray-500 dark:text-slate-400">
 Username login: <span className="font-mono text-indigo-600 dark:text-indigo-400" data-testid="reset-modal-username">{resetOwner.login_username || resetOwner.owner_code}</span>
 </p>
 </div>
 </div>
 </div>

 {/* Password Input */}
 <div className="space-y-2">
 <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">
 Password Baru
 </label>
 <Input
 type="text"
 placeholder="Kosongkan untuk default: 12345678"
 data-testid="owner-reset-password-input"
 value={newPassword}
 onChange={(e) => setNewPassword(e.target.value)}
 className="h-10"
 />
 <p className="text-xs text-gray-500 dark:text-slate-400">
 💡 Kosongkan field untuk menggunakan password default
 </p>
 </div>
 </div>

 {/* Footer */}
 <div className="px-5 py-4 bg-gray-50 flex gap-3 dark:bg-slate-900">
 <Button
 variant="outline"
 onClick={() => setShowResetModal(false)}
 className="flex-1"
 disabled={resetting}
 >
 Batal
 </Button>
 <Button
 onClick={handleResetPassword}
 disabled={resetting}
 className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
 data-testid="owner-reset-password-submit"
 >
 {resetting ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
 Menyimpan...
 </>
 ) : (
 <>
 <Key className="h-4 w-4 mr-2" />
 Reset Password
 </>
 )}
 </Button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default OwnersManagement;
